import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Textarea,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '@/hooks/useToast'
import { authService } from '@/services/authService'
import type { AdminUser } from '@/types'

interface AdminMfaResetModalProps {
  isOpen: boolean
  onClose: () => void
  user: AdminUser | null
  onSuccess: () => void
}

export function AdminMfaResetModal({
  isOpen,
  onClose,
  user,
  onSuccess,
}: AdminMfaResetModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const [totp, setTotp] = useState('')
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const close = () => {
    if (isSubmitting) {
      return
    }
    setTotp('')
    setReason('')
    onClose()
  }

  const handleReset = async () => {
    if (!user || !/^\d{6}$/.test(totp) || !reason.trim()) {
      return
    }
    setIsSubmitting(true)
    try {
      await authService.verifyMfa(totp)
      await authService.resetAdminMfa(user.id, reason.trim())
      toast.success({ titleKey: 'adminUsers.mfaReset.success' })
      onSuccess()
      setTotp('')
      setReason('')
      onClose()
    } catch {
      toast.error({
        titleKey: 'adminUsers.mfaReset.failed',
        descriptionKey: 'adminUsers.mfaReset.failedDescription',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="md">
        <ModalHeader>{t('adminUsers.mfaReset.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Alert status="warning" alignItems="flex-start">
              <AlertIcon />
              <AlertDescription>
                {t('adminUsers.mfaReset.warning', { email: user?.email ?? '' })}
              </AlertDescription>
            </Alert>
            <FormControl isRequired>
              <FormLabel>{t('adminUsers.mfaReset.yourCode')}</FormLabel>
              <Input
                value={totp}
                onChange={(event) =>
                  setTotp(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder={t('auth.mfa.codePlaceholder')}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{t('adminUsers.mfaReset.reason')}</FormLabel>
              <Textarea
                value={reason}
                onChange={(event) => setReason(event.target.value.slice(0, 500))}
                placeholder={t('adminUsers.mfaReset.reasonPlaceholder')}
                maxLength={500}
                resize="vertical"
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={close} isDisabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="red"
            onClick={handleReset}
            isLoading={isSubmitting}
            isDisabled={!/^\d{6}$/.test(totp) || !reason.trim()}
          >
            {t('adminUsers.mfaReset.action')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
